import { useState } from 'react'
import { useLoginByKey, LoginKeyError } from '../api'
import { sanitizeAccessKeyInput, formatAccessKey, isCompleteAccessKey } from '../lib/accessKey'

export function UserLoginPage() {
  const loginByKey = useLoginByKey()
  const [normalized, setNormalized] = useState('')
  const [showForbiddenHint, setShowForbiddenHint] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const display = formatAccessKey(normalized)
  const canSubmit = isCompleteAccessKey(normalized) && !loginByKey.isPending

  const handleChange = (raw: string) => {
    const { value, filteredForbidden } = sanitizeAccessKeyInput(raw)
    setNormalized(value)
    if (filteredForbidden) setShowForbiddenHint(true)
    if (toast) setToast(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    loginByKey.mutate(normalized, {
      // Hard redirect (full document load), not client-side navigate: a reload
      // discards the React Query cache so the freshly-authenticated operator
      // can't be shown the previous account's cached ['me']/['phones']/['calls'].
      // Mirrors logout() in api/client.ts.
      onSuccess: () => { window.location.href = '/app' },
      onError: (err) => {
        if (err instanceof LoginKeyError) {
          if (err.status === 429) setToast('Слишком много попыток, попробуйте через минуту')
          else if (err.status === 0) setToast('Нет соединения с сервером')
          else setToast('Ключ недействителен')
        } else {
          setToast('Ключ недействителен')
        }
      },
    })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-semibold text-center text-gray-900 dark:text-gray-100 mb-2">
          МММ
        </h1>
        <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-8 font-mono">
          введите ключ доступа
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" aria-label="Access key login">
          <div>
            <label htmlFor="access-key" className="sr-only">Access key</label>
            <input
              id="access-key"
              type="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="XK4P-9M2H-RT7L"
              value={display}
              onChange={e => handleChange(e.target.value)}
              aria-invalid={Boolean(toast)}
              aria-describedby={showForbiddenHint ? 'access-key-hint' : undefined}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700
                bg-white dark:bg-gray-900 px-4 py-3
                text-center font-mono tracking-[0.15em] text-lg uppercase
                text-gray-900 dark:text-gray-100
                placeholder-gray-400 dark:placeholder-gray-600
                focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {showForbiddenHint && (
              <p id="access-key-hint" className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 text-center">
                Используйте цифры и буквы (без I, L, O, U)
              </p>
            )}
          </div>

          {toast && (
            <p
              role="alert"
              className="text-sm text-center text-red-500"
            >
              {toast}
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed
              text-white font-medium py-2.5 transition-colors"
          >
            {loginByKey.isPending ? 'Входим...' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  )
}
